import {
  useState,
  useEffect,
  forwardRef,
  type InputHTMLAttributes,
} from 'react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import {
  PHONE_COUNTRIES,
  DEFAULT_PHONE_COUNTRY,
  flagEmoji,
  phoneCountryByIso,
  resolvePhoneCountry,
} from '@shared/phoneCountries'

// ---------------------------------------------------------------------------
// Dial codes
// ---------------------------------------------------------------------------

// Unique dial codes sorted longest-first for greedy prefix matching
const UNIQUE_CODES = [...new Set(PHONE_COUNTRIES.map(c => c.dialCode))].sort(
  (a, b) => b.length - a.length
)

function dialCodeOf(iso: string): string {
  return phoneCountryByIso(iso)?.dialCode ?? '+1'
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Format up to 10 local digits progressively as (XXX) XXX-XXXX */
function formatLocal(digits: string): string {
  const d = digits.slice(0, 10)
  if (d.length === 0) return ''
  if (d.length <= 3) return `(${d}`
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
}

/** Extract dial code and raw local digits from a stored value string */
function parseValue(raw: string): { code: string; localDigits: string } {
  if (!raw) return { code: '+1', localDigits: '' }

  for (const code of UNIQUE_CODES) {
    if (raw.startsWith(code)) {
      const localDigits = raw.slice(code.length).replace(/\D/g, '').slice(0, 10)
      return { code, localDigits }
    }
  }

  // Fallback: assume +1, extract digits
  const digits = raw.replace(/\D/g, '')
  const localDigits = digits.startsWith('1')
    ? digits.slice(1, 11)
    : digits.slice(0, 10)
  return { code: '+1', localDigits }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface PhoneInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> {
  value?: string
  onChange?: (value: string) => void
  /** Saved ISO country (`phoneCountry`), e.g. "CA". */
  country?: string
  /** Called with the chosen ISO country whenever a number is emitted. */
  onCountryChange?: (iso: string) => void
}

/**
 * Phone input — country code select + local number input.
 *
 * - Defaults to +1 US
 * - Local number formatted as (XXX) XXX-XXXX (10 digits, all codes)
 * - onChange emits a single string: "+1 (555) 123-4567"
 * - onCountryChange emits the chosen ISO country ("CA"), which the form saves
 *   as `phoneCountry` (PB-66): US and CA share +1, so the number alone can't
 *   say which was picked. Countries come from `@shared/phoneCountries`.
 * - Compatible with React Hook Form: field.onChange receives a string
 */
export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    {
      value = '',
      onChange,
      country,
      onCountryChange,
      className,
      disabled,
      ...props
    },
    ref
  ) => {
    const { code: parsedCode, localDigits } = parseValue(value)

    // Track the country independently so the user can switch it while the
    // local number is empty
    const [selectedIso, setSelectedIso] = useState<string>(() =>
      value
        ? resolvePhoneCountry(parsedCode, country).iso
        : (phoneCountryByIso(country)?.iso ?? DEFAULT_PHONE_COUNTRY)
    )

    // Sync when the value or saved country changes from outside (e.g.
    // form.reset). Keep the current pick when it still matches the number's
    // dial code, so typing doesn't flip CA back to US.
    useEffect(() => {
      if (!value) return // keep the user's pick on clear
      const code = parseValue(value).code
      setSelectedIso(current =>
        phoneCountryByIso(country)?.dialCode === code
          ? (country as string)
          : dialCodeOf(current) === code
            ? current
            : resolvePhoneCountry(code).iso
      )
    }, [value, country])

    const handleCodeChange = (iso: string) => {
      setSelectedIso(iso)
      if (localDigits) {
        onChange?.(dialCodeOf(iso) + ' ' + formatLocal(localDigits))
        onCountryChange?.(iso)
      }
    }

    const handleLocalChange = (e: { target: HTMLInputElement }) => {
      const newRaw = e.target.value
      const newDigits = newRaw.replace(/\D/g, '').slice(0, 10)
      const oldDisplay = formatLocal(localDigits)

      let finalDigits = newDigits

      // Backspace trap: one character gone but the same digits means the user
      // deleted a formatting character — strip one digit to make progress.
      // Only exactly one: pasting a whole new number over an old one is also
      // shorter with the same digit count, and must keep every digit.
      if (
        newDigits.length === localDigits.length &&
        newRaw.length === oldDisplay.length - 1
      ) {
        finalDigits = newDigits.slice(0, -1)
      }

      const dialCode = dialCodeOf(selectedIso)
      onChange?.(finalDigits ? dialCode + ' ' + formatLocal(finalDigits) : '')
      onCountryChange?.(finalDigits ? selectedIso : '')
    }

    return (
      <div className={cn('flex', className)}>
        <Select
          value={selectedIso}
          onValueChange={handleCodeChange}
          disabled={disabled}
        >
          <SelectTrigger className="w-[130px] rounded-r-none border-r-0 focus:z-10 shrink-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent position="item-aligned">
            {PHONE_COUNTRIES.map(({ iso, dialCode, label }) => (
              <SelectItem key={iso} value={iso}>
                {dialCode} {label} {flagEmoji(iso)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          ref={ref}
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder="(555) 123-4567"
          {...props}
          disabled={disabled}
          className="rounded-l-none flex-1"
          value={formatLocal(localDigits)}
          onChange={handleLocalChange}
        />
      </div>
    )
  }
)

PhoneInput.displayName = 'PhoneInput'
